import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { formatSingaporeDate } from "@shared/timezone";
import type { GlobalSearchHit, ServiceRecordSearchPayload } from "@shared/globalSearch";
import { Loader2, Package, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";

function statusLabel(status: string): string {
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function isServicePayload(p: GlobalSearchHit["payload"]): p is ServiceRecordSearchPayload {
  return (
    !!p &&
    typeof p === "object" &&
    "kind" in p &&
    ((p as ServiceRecordSearchPayload).kind === "svr" ||
      (p as ServiceRecordSearchPayload).kind === "ro")
  );
}

type ProductSearchPayload = {
  serialNumber: string;
  brand: string;
  model: string;
  customerName: string | null;
  warrantyRecordState: "draft" | "issued" | "none";
  warrantyExpiry: string | null;
};

function isProductPayload(
  hit: GlobalSearchHit,
): hit is GlobalSearchHit & { payload: ProductSearchPayload } {
  const payload = hit.payload as Partial<ProductSearchPayload> | null;
  return (
    hit.entityType === "product" &&
    !!payload &&
    typeof payload.serialNumber === "string" &&
    typeof payload.brand === "string" &&
    typeof payload.model === "string"
  );
}

export function ServiceDashboardGlobalSearch() {
  const [, setLocation] = useLocation();
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(input.trim()), 250);
    return () => window.clearTimeout(id);
  }, [input]);

  const enabled = debounced.length >= 1;
  const { data, isFetching, isError } = trpc.globalSearch.search.useQuery(
    { query: debounced, limit: 25, provider: "service" },
    { enabled, staleTime: 15_000 },
  );

  const hits = useMemo(() => data?.hits ?? [], [data?.hits]);
  const showResults = enabled;

  return (
    <section className="rounded-xl border border-slate-200/80 bg-white shadow-sm">
      <div className="p-4 sm:p-5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search customer, address, SVR, RO, equipment or serial number..."
            aria-label="Global service search"
            className="h-12 border-slate-200 bg-slate-50/60 pl-10 pr-10 text-base shadow-none focus-visible:bg-white"
            autoComplete="off"
            spellCheck={false}
          />
          {input ? (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              onClick={() => setInput("")}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        {!showResults ? (
          <p className="mt-2 text-xs text-slate-400">
            Find any service visit or repair order without knowing where it lives.
          </p>
        ) : null}
      </div>

      {showResults ? (
        <div className="border-t border-slate-100">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 sm:px-5">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {isFetching && hits.length === 0
                ? "Searching…"
                : `${hits.length} result${hits.length === 1 ? "" : "s"}`}
            </p>
            {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /> : null}
          </div>

          {isError ? (
            <p className="px-4 pb-4 text-sm text-red-600 sm:px-5">
              Search failed. Please try again.
            </p>
          ) : hits.length === 0 && !isFetching ? (
            <p className="px-4 pb-5 text-sm text-slate-500 sm:px-5">
              No service records match “{debounced}”.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] text-sm">
                <thead className="bg-slate-50/80 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 font-medium sm:px-5">SVR / RO</th>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                    <th className="px-3 py-2.5 font-medium">Customer</th>
                    <th className="px-3 py-2.5 font-medium">Project</th>
                    <th className="px-3 py-2.5 font-medium">Address</th>
                    <th className="px-3 py-2.5 font-medium">Scheduled</th>
                    <th className="px-3 py-2.5 font-medium">Technician</th>
                    <th className="px-4 py-2.5 font-medium sm:px-5">Matched</th>
                  </tr>
                </thead>
                <tbody>
                  {hits.map((hit) => {
                    if (isProductPayload(hit)) {
                      const payload = hit.payload;
                      const warrantyLabel =
                        payload.warrantyRecordState === "draft"
                          ? "Warranty draft"
                          : payload.warrantyRecordState === "issued"
                            ? "Issued"
                            : null;
                      return (
                        <tr
                          key={`product-${hit.entityId}`}
                          className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50/80"
                          onClick={() => setLocation(hit.href)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setLocation(hit.href);
                            }
                          }}
                          tabIndex={0}
                          role="link"
                        >
                          <td colSpan={8} className="px-4 py-3 sm:px-5">
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                              <div className="flex min-w-[240px] items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
                                  <Package className="h-4 w-4 text-slate-500" />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">
                                    {payload.brand} {payload.model}
                                  </p>
                                  <p className="font-mono text-xs text-slate-500">
                                    S/N {payload.serialNumber}
                                  </p>
                                </div>
                              </div>
                              <div className="min-w-[180px]">
                                <p className="text-[10px] uppercase tracking-wide text-slate-400">
                                  Customer
                                </p>
                                <p className="text-sm text-slate-700">
                                  {payload.customerName || "Unassigned"}
                                </p>
                              </div>
                              <div className="flex items-center gap-3">
                                {warrantyLabel ? (
                                  <Badge variant="outline" className="text-[10px]">
                                    {warrantyLabel}
                                  </Badge>
                                ) : null}
                                <p className="text-xs text-slate-600">
                                  {payload.warrantyExpiry
                                    ? `Expires ${formatSingaporeDate(payload.warrantyExpiry, {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                      })}`
                                    : "No warranty expiry"}
                                </p>
                              </div>
                              <div className="ml-auto min-w-[140px]">
                                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                  {hit.matchedField.label}
                                </p>
                                <p className="truncate text-xs text-slate-700">
                                  {hit.matchedField.value}
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    const payload = isServicePayload(hit.payload) ? hit.payload : null;
                    const reference = payload?.reference ?? hit.subtitle ?? String(hit.entityId);
                    const kind = payload?.kind ?? "svr";
                    return (
                      <tr
                        key={`${hit.entityType}-${kind}-${hit.entityId}`}
                        className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50/80"
                        onClick={() => setLocation(hit.href)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setLocation(hit.href);
                          }
                        }}
                        tabIndex={0}
                        role="link"
                      >
                        <td className="px-4 py-3 sm:px-5">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-semibold text-slate-800">
                              {reference}
                            </span>
                            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                              {kind === "ro" ? "RO" : "SVR"}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          {statusLabel(payload?.status ?? "—")}
                        </td>
                        <td className="px-3 py-3 font-medium text-slate-900">
                          {payload?.customerName ?? hit.title}
                        </td>
                        <td className="px-3 py-3 text-slate-600">
                          {payload?.projectName ?? "—"}
                        </td>
                        <td className="max-w-[220px] truncate px-3 py-3 text-slate-600">
                          {payload?.address ?? "—"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                          {payload?.scheduledDate
                            ? formatSingaporeDate(payload.scheduledDate, {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                        <td className="px-3 py-3 text-slate-600">
                          {payload?.assignedTechnician ?? "—"}
                        </td>
                        <td className="px-4 py-3 sm:px-5">
                          <div className="min-w-[140px]">
                            <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                              {hit.matchedField.label}
                            </p>
                            <p className="truncate text-xs text-slate-700">{hit.matchedField.value}</p>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
